import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGE_CODES, getLanguageMeta } from '../i18n/languages';

const SITE_URL = 'https://offshoreadvisory.pro';

const hreflangForCode = (code) => {
    const meta = getLanguageMeta(code);
    if (meta.htmlLang === 'zh-CN') return 'zh-CN';
    return meta.htmlLang;
};

const ogLocaleForCode = (code) => {
    const map = { en: 'en_US', zh: 'zh_CN', tr: 'tr_TR' };
    return map[code] || 'en_US';
};

/**
 * SEO head manager.
 *
 * react-helmet-async is not reflecting tags into <head> in this app, so the
 * document head is managed imperatively here. Every tag this component owns is
 * marked with `data-seo` and rewritten on each render, so switching pages (or
 * language) never leaves stale or duplicated meta behind.
 *
 * The `url` prop is the language-neutral path (e.g. "/banking-license"); the
 * language variants and reciprocal hreflang alternates are derived from it.
 */
export const SEO = ({
    title,
    description,
    keywords,
    author = 'Offshore Advisory',
    url,
    type = 'website',
    robots = 'index, follow',
}) => {
    const { i18n } = useTranslation();
    const rawLang = (i18n.language || 'en').split('-')[0];
    const lang = SUPPORTED_LANGUAGE_CODES.includes(rawLang) ? rawLang : 'en';

    useEffect(() => {
        const path = url || '/';
        const urls = Object.fromEntries(
            SUPPORTED_LANGUAGE_CODES.map((code) => [
                code,
                path === '/' ? `${SITE_URL}/${code}` : `${SITE_URL}/${code}${path}`,
            ])
        );
        const currentUrl = urls[lang];
        const ogLocale = ogLocaleForCode(lang);
        const altLocales = SUPPORTED_LANGUAGE_CODES.filter((c) => c !== lang).map(ogLocaleForCode);

        const head = document.head;
        head.querySelectorAll('[data-seo]').forEach((n) => n.remove());

        const add = (tag, attrs) => {
            const el = document.createElement(tag);
            el.setAttribute('data-seo', '');
            Object.entries(attrs).forEach(([k, v]) => {
                if (v != null) el.setAttribute(k, v);
            });
            head.appendChild(el);
        };

        if (title) document.title = title;

        if (title) add('meta', { name: 'title', content: title });
        if (description) add('meta', { name: 'description', content: description });
        if (keywords) add('meta', { name: 'keywords', content: keywords });
        if (author) add('meta', { name: 'author', content: author });
        add('meta', { name: 'robots', content: robots });

        add('meta', { property: 'og:type', content: type });
        add('meta', { property: 'og:url', content: currentUrl });
        if (title) add('meta', { property: 'og:title', content: title });
        if (description) add('meta', { property: 'og:description', content: description });
        add('meta', { property: 'og:locale', content: ogLocale });
        altLocales.forEach((locale) => {
            add('meta', { property: 'og:locale:alternate', content: locale });
        });

        add('meta', { property: 'twitter:card', content: 'summary_large_image' });
        add('meta', { property: 'twitter:url', content: currentUrl });
        if (title) add('meta', { property: 'twitter:title', content: title });
        if (description) add('meta', { property: 'twitter:description', content: description });

        add('link', { rel: 'canonical', href: currentUrl });
        SUPPORTED_LANGUAGE_CODES.forEach((code) => {
            add('link', { rel: 'alternate', hreflang: hreflangForCode(code), href: urls[code] });
        });
        add('link', { rel: 'alternate', hreflang: 'x-default', href: urls.en });
    }, [title, description, keywords, author, url, type, robots, lang]);

    return null;
};

export default SEO;
